import React, { FC, useCallback, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Flex, getIcon, IconType, IdentifierDTO, Plus, SecondaryButton, Semibold, useModal } from '@zazume/zzm-base';

import { useI18n } from '#/hooks/useI18n';
import MetricsHelper from '#/utils/metrics/metricsHelper';

import { Document, DocumentCategories, RelatedModelType } from '../../models';
import { EmptyState } from '../other/EmptyState';
import { Can } from '../roles/Can';
import { DropdownMenu } from './atoms/DropdownMenu';
import { DocumentCard } from './DocumentCard';
import { DocumentModal } from './DocumentModal';
import { getNavigationState } from './helper';

const Container = styled.div`
  width: 100%;
  padding-bottom: 32px;
`;

const Top = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    ${theme.mq.sm} {
      flex-direction: row;
      align-items: center;
    }
  `
);

const TopAction = styled.div(
  ({ theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xs};
    white-space: nowrap;
  `
);

const DocumentsContainer = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(1fr);
    gap: 20px;
    padding-top: 20px;
    min-height: 77px;

    ${theme.mq.sm} {
      grid-template-columns: repeat(2, 1fr);
    }
  `
);

export interface DocumentUploaderProps {
  documents: Document[];
  icon: IconType;
  title: string;
  onUpdate?: (document: Document) => void;
  onRemove?: (document: Document) => void;
  onUpload?: () => void;
  buttonSize?: 'Medium' | 'Large';
  relatedModel: RelatedModelType;
  relatedModelId: IdentifierDTO;
  category: DocumentCategories;
}

export const DocumentUploader: FC<DocumentUploaderProps> = ({
  icon,
  title,
  documents,
  onUpload,
  onRemove,
  onUpdate,
  buttonSize = 'Medium',
  category,
  relatedModel,
  relatedModelId
}) => {
  const { t } = useI18n();
  const Icon = getIcon(icon);
  const [currentDocument, setCurrentDocument] = useState<Document | null>(null);
  const { RenderModal, open, close } = useModal({ modalSize: 'l' });

  const isEditable = Boolean(onUpload);

  const handleUpload = () => {
    MetricsHelper.track('Upload Document Btn', { category });
    onUpload?.();
  };

  const handleOpenDocument = useCallback(
    (document: Document) => {
      setCurrentDocument(document);
      open();
    },
    [open]
  );

  const onNextClick = useCallback(
    (id: string) => {
      const currentIndex = documents.findIndex(doc => doc._id === id);
      if (currentIndex === -1 || currentIndex === documents.length - 1) {
        return;
      }
      const nextDocument = documents[currentIndex + 1];
      setCurrentDocument(nextDocument);
    },
    [documents]
  );

  const onPreviousClick = useCallback(
    (id: string) => {
      const currentIndex = documents.findIndex(doc => doc._id === id);
      if (currentIndex === -1 || currentIndex === 0) {
        return;
      }
      const previousDocument = documents[currentIndex - 1];
      setCurrentDocument(previousDocument);
    },
    [documents]
  );

  const handleCloseModal = useCallback(() => {
    close();
    setCurrentDocument(null);
  }, [close]);

  const navigationState = getNavigationState(documents, currentDocument);

  return (
    <Container>
      <Top>
        <Flex align="center" gap="xs">
          <Icon color="Primary" size={32} />
          <Semibold variant="XL">{title}</Semibold>
        </Flex>
        {isEditable && (
          <TopAction>
            <SecondaryButton size={buttonSize} onClick={handleUpload}>
              <Plus color="Primary" size={16} /> {t('documents.newDocument')}
            </SecondaryButton>
            <Can hasRoles={['admin', 'buildingManager']}>
              <DropdownMenu relatedModel={relatedModel} relatedModelId={relatedModelId} category={category} />
            </Can>
          </TopAction>
        )}
      </Top>
      {!documents.length ? (
        isEditable ? (
          <EmptyState text={t('documents.emptyState')} linkText={t('general.clickHere')} onClick={onUpload} />
        ) : (
          <EmptyState text={t('documents.emptyStateNotEditable')} />
        )
      ) : (
        <DocumentsContainer>
          {documents.map(document => (
            <DocumentCard
              documentId={document._id}
              onRemove={onRemove}
              onUpdate={onUpdate}
              key={document._id}
              onOpenDocument={handleOpenDocument}
            />
          ))}
        </DocumentsContainer>
      )}
      {currentDocument && (
        <RenderModal>
          <DocumentModal
            document={currentDocument}
            close={handleCloseModal}
            onNextClick={onNextClick}
            onPreviousClick={onPreviousClick}
            hasNext={navigationState.hasNext}
            hasPrevious={navigationState.hasPrevious}
          />
        </RenderModal>
      )}
    </Container>
  );
};
